/**
 * Suscripciones y pagos fijos: Netflix, Spotify, el gimnasio, el plan del
 * celular, el internet. Lo que se cobra solo o hay que pagar cada tanto.
 *
 * ── POR QUÉ MERECEN SU PROPIO MOTOR ─────────────────────────────────────────
 *
 * Porque son la plata que se va sin que uno la decida cada vez. Un gasto
 * normal se piensa antes de hacerlo; una suscripción se pensó UNA vez, hace
 * meses, y sigue cobrando. Por eso la pregunta útil no es "¿cuánto gasté?"
 * sino "¿cuánto se me va al mes en cosas fijas, y al año?".
 *
 * ── LAS TRES FORMAS DE PAGARLAS, QUE NO SE ANOTAN IGUAL ─────────────────────
 *
 *   SE COBRA SOLA A LA TARJETA (Netflix en Nu)
 *     Pasa aunque uno no haga nada. La app la anota sola el día del cobro,
 *     contra la tarjeta. No hay 4x1000: es crédito.
 *
 *   DÉBITO AUTOMÁTICO DE UNA CUENTA (el gym del Banco de Bogotá)
 *     También pasa solo, pero sale del banco: ahí SÍ hay 4x1000, y si no hay
 *     saldo el cobro rebota. La app la anota sola, con su impuesto.
 *
 *   LA PAGAS TÚ (el gym en efectivo, el internet por PSE)
 *     No pasa hasta que uno paga. La app no la anota sola: recuerda, y uno
 *     dice "ya lo pagué" o "este mes no".
 *
 * ── POR QUÉ LOS COBROS TIENEN UN ID FIJO ────────────────────────────────────
 *
 * Si el celular y el PC anotaran cada uno el cobro de Netflix del 17, al
 * sincronizar habría dos. Por eso el id del cobro no es aleatorio: sale de
 * la suscripción y de la fecha. Los dos aparatos llegan al MISMO id, y la
 * base los junta en uno. Y si uno borra un cobro ("este mes no me cobraron"),
 * queda la marca de borrado con ese id, y nadie lo vuelve a crear.
 *
 * El id conserva la forma de UUIDv7 con la fecha del cobro adelante, para que
 * al ordenar por id los cobros queden en su lugar en el tiempo y no se amontonen.
 */

import { sumarMeses, hoyISO } from './cuotas.js';

export const FRECUENCIA = {
  SEMANAL: 'semanal',
  QUINCENAL: 'quincenal',
  MENSUAL: 'mensual',
  BIMESTRAL: 'bimestral',
  TRIMESTRAL: 'trimestral',
  SEMESTRAL: 'semestral',
  ANUAL: 'anual',
};

export const COBRO = {
  TARJETA: 'tarjeta',              // se cobra sola a una tarjeta de crédito
  DEBITO: 'debito_automatico',     // se debita sola de una cuenta
  MANUAL: 'manual',                // la pagas tú
};

const MESES = { mensual: 1, bimestral: 2, trimestral: 3, semestral: 6, anual: 12 };
const DIAS = { semanal: 7, quincenal: 15 };

/** Cuántas veces al mes pesa cada frecuencia. Para el costo mensual. */
const POR_MES = {
  semanal: 52 / 12,
  quincenal: 2,
  mensual: 1,
  bimestral: 1 / 2,
  trimestral: 1 / 3,
  semestral: 1 / 6,
  anual: 1 / 12,
};

const ETIQUETA_FRECUENCIA = {
  semanal: 'a la semana',
  quincenal: 'a la quincena',
  mensual: 'al mes',
  bimestral: 'cada dos meses',
  trimestral: 'cada tres meses',
  semestral: 'cada seis meses',
  anual: 'al año',
};

export function etiquetaFrecuencia(f) {
  return ETIQUETA_FRECUENCIA[f] ?? f;
}

// ─── Ids que no dependen del aparato ────────────────────────────────────────

/** Hash de 53 bits (cyrb53). Rápido, sin dependencias y el mismo en todos lados. */
function cyrb53(texto, semilla = 0) {
  let h1 = 0xdeadbeef ^ semilla;
  let h2 = 0x41c6ce57 ^ semilla;
  for (let i = 0; i < texto.length; i++) {
    const ch = texto.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/**
 * Un id con forma de UUIDv7, pero calculado: misma fecha y misma semilla,
 * mismo id, en cualquier aparato. Los primeros 48 bits son el mediodía de
 * esa fecha en Colombia (UTC−5, sin horario de verano), así ordena bien.
 */
export function idDeterminista(fecha, semilla) {
  const ms = new Date(`${fecha}T12:00:00-05:00`).getTime();
  const t = ms.toString(16).padStart(12, '0').slice(-12);
  const r = (cyrb53(semilla, 1).toString(16).padStart(14, '0') +
             cyrb53(semilla, 2).toString(16).padStart(14, '0')).slice(0, 20);
  const variante = ((parseInt(r[4], 16) & 0x3) | 0x8).toString(16);
  return `${t.slice(0, 8)}-${t.slice(8, 12)}-7${r.slice(0, 3)}-${variante}${r.slice(5, 8)}-${r.slice(8, 20)}`;
}

/** El id del cobro de una suscripción en una fecha. */
export function idDelCobro(rec, fecha) {
  return idDeterminista(fecha, `cobro:${rec.id}:${fecha}`);
}

/** El id del 4x1000 de ese cobro: también fijo, por la misma razón. */
export function idDelGmfDelCobro(rec, fecha) {
  return idDeterminista(fecha, `gmf:${rec.id}:${fecha}`);
}

// ─── Fechas ─────────────────────────────────────────────────────────────────

function sumarDias(fechaIso, n) {
  const [a, m, d] = fechaIso.split('-').map(Number);
  const f = new Date(a, m - 1, d + n);
  return `${f.getFullYear()}-${String(f.getMonth() + 1).padStart(2, '0')}-${String(f.getDate()).padStart(2, '0')}`;
}

function enPausa(rec, fecha) {
  if (!rec.pausada_desde) return false;
  return fecha >= rec.pausada_desde && (!rec.pausada_hasta || fecha <= rec.pausada_hasta);
}

/**
 * Las fechas de cobro entre dos días (los dos incluidos).
 *
 * Arranca en `fecha_inicio` (el primer cobro de verdad) y avanza según la
 * frecuencia. El día 31 se corre al último día de los meses cortos, como
 * hacen los bancos. No cuenta lo que cae después de cancelar ni durante una
 * pausa.
 */
export function fechasDeCobro(rec, desde, hasta) {
  const inicio = rec?.fecha_inicio;
  if (!inicio || !hasta || hasta < inicio) return [];
  const fechas = [];

  if (MESES[rec.frecuencia]) {
    const dia = Number(inicio.slice(8, 10));
    for (let k = 0; k < 2400; k++) {
      const f = sumarMeses(inicio, k * MESES[rec.frecuencia], dia);
      if (f > hasta) break;
      if (f >= desde) fechas.push(f);
    }
  } else {
    const paso = DIAS[rec.frecuencia] ?? 30;
    for (let k = 0; k < 10000; k++) {
      const f = sumarDias(inicio, k * paso);
      if (f > hasta) break;
      if (f >= desde) fechas.push(f);
    }
  }

  return fechas.filter((f) =>
    (!rec.cancelada_en || f < rec.cancelada_en) && !enPausa(rec, f));
}

/** El próximo cobro a partir de hoy (incluido), o null si ya no cobra. */
export function proximoCobro(rec, hoy = hoyISO()) {
  if (rec.borrado_en) return null;
  const hasta = sumarMeses(hoy, 25);
  return fechasDeCobro(rec, hoy, hasta)[0] ?? null;
}

// ─── Estado ─────────────────────────────────────────────────────────────────

/**
 * En qué está una suscripción hoy.
 *
 *   prueba     todavía no cobra: está en el periodo gratis
 *   activa     cobra normal
 *   pausada    no cobra por un tiempo (el gym en vacaciones)
 *   cancelada  ya no cobra
 */
export function estadoDe(rec, hoy = hoyISO()) {
  if (rec.cancelada_en && rec.cancelada_en <= hoy) return 'cancelada';
  if (enPausa(rec, hoy)) return 'pausada';
  if (rec.prueba_gratis && hoy < rec.fecha_inicio) return 'prueba';
  return 'activa';
}

/** Lo que cuesta al mes, aunque se pague cada año o cada semana. */
export function costoMensual(rec) {
  return Math.round((rec.monto || 0) * (POR_MES[rec.frecuencia] ?? 1));
}

// ─── Cobros ─────────────────────────────────────────────────────────────────

/**
 * Los cobros que ya debieron pasar, desde que se anotó la suscripción.
 *
 * No se inventa el pasado: si alguien anota hoy un Netflix que paga desde
 * hace tres años, la app no le crea 36 gastos viejos. Solo cuenta desde el
 * día en que se registró (`registrada_en`). `hasta` permite mirar unos días
 * adelante para los recordatorios de lo que se paga a mano.
 */
export function cobrosDebidos(rec, hasta = hoyISO()) {
  if (rec.borrado_en) return [];
  const desde = rec.registrada_en && rec.registrada_en > rec.fecha_inicio
    ? rec.registrada_en
    : rec.fecha_inicio;
  const saltados = new Set(rec.saltados ?? []);
  return fechasDeCobro(rec, desde, hasta).filter((f) => !saltados.has(f));
}

/** Método de pago que queda en el movimiento, según cómo se cobra. */
export function metodoDelCobro(rec) {
  if (rec.cobro === COBRO.TARJETA) return 'credito';
  if (rec.cobro === COBRO.DEBITO) return 'debito_automatico';
  return rec.metodo_pago || 'efectivo';
}

/**
 * El movimiento de un cobro. Descripción pura: la pantalla lo guarda.
 *
 * Si el monto cambia cada mes (en dólares, o por consumo), el movimiento
 * entra como NO revisado, igual que lo que lee la IA de una foto: la cifra
 * es una estimación hasta que la persona la confirme con el extracto.
 */
export function movimientoDelCobro(rec, fecha, extra = {}) {
  return {
    id: idDelCobro(rec, fecha),
    tipo: 'egreso',
    monto: Math.round(rec.monto || 0),
    fecha,
    concepto: rec.nombre,
    contraparte: rec.nombre,
    categoria_id: rec.categoria_id ?? null,
    cuenta_origen: rec.cuenta_id ?? null,
    cuenta_destino: null,
    metodo_pago: metodoDelCobro(rec),
    recurrente_id: rec.id,
    ciclo: fecha,
    origen_dato: 'recurrente',
    revisado: !rec.monto_variable,
    ...extra,
  };
}

/**
 * Lo que la app tiene que anotar sola: los cobros automáticos que ya
 * pasaron. Los que se pagan a mano no se anotan solos nunca.
 *
 * `existentes` son los ids que ya están en la base, vivos o borrados: un
 * cobro borrado a propósito no se vuelve a crear.
 */
export function cobrosParaAnotar(recs, existentes = new Set(), hoy = hoyISO()) {
  const salida = [];
  for (const rec of recs) {
    if (rec.borrado_en || rec.cobro === COBRO.MANUAL) continue;
    for (const f of cobrosDebidos(rec, hoy)) {
      const id = idDelCobro(rec, f);
      if (!existentes.has(id)) salida.push({ rec, fecha: f, id });
    }
  }
  return salida;
}

/**
 * Lo que toca pagar a mano: vencido o por vencer en los próximos días.
 * Cada uno sabe cuántos días le faltan (negativo = ya se pasó).
 */
export function porPagar(recs, existentes = new Set(), hoy = hoyISO()) {
  const salida = [];
  for (const rec of recs) {
    if (rec.borrado_en || rec.cobro !== COBRO.MANUAL) continue;
    const aviso = Math.max(0, rec.avisar_dias_antes ?? 3);
    const hasta = sumarDias(hoy, aviso);
    for (const f of cobrosDebidos(rec, hasta)) {
      if (existentes.has(idDelCobro(rec, f))) continue;
      salida.push({ rec, fecha: f, id: idDelCobro(rec, f), dias: diasHasta(hoy, f) });
    }
  }
  return salida.sort((a, b) => a.fecha.localeCompare(b.fecha));
}

function diasHasta(hoy, fecha) {
  return Math.round(
    (new Date(fecha + 'T12:00:00') - new Date(hoy + 'T12:00:00')) / 86400000);
}

/** Los próximos cobros de todas, en los siguientes `dias` días. */
export function proximosCobros(recs, hoy = hoyISO(), dias = 30) {
  const hasta = sumarDias(hoy, dias);
  const salida = [];
  for (const rec of recs) {
    if (rec.borrado_en) continue;
    for (const f of fechasDeCobro(rec, hoy, hasta)) {
      if ((rec.saltados ?? []).includes(f)) continue;
      salida.push({ rec, fecha: f, dias: diasHasta(hoy, f), monto: rec.monto });
    }
  }
  return salida.sort((a, b) => a.fecha.localeCompare(b.fecha));
}

// ─── Mirarlas todas juntas ──────────────────────────────────────────────────

/**
 * El resumen que importa: cuánto se va al mes en cosas fijas, y al año.
 *
 * El número anual es el que hace pensar. "$26.900 al mes" no asusta a nadie;
 * "$322.800 al año" por una plataforma que se usa dos veces al mes, sí.
 */
export function resumenFijos(recs, hoy = hoyISO()) {
  const vivas = recs.filter((r) => !r.borrado_en);
  const porEstado = { activa: [], prueba: [], pausada: [], cancelada: [] };
  for (const r of vivas) porEstado[estadoDe(r, hoy)].push(r);

  // Lo que está en prueba cuenta: va a empezar a cobrar, y hay que verlo venir.
  const cobrando = [...porEstado.activa, ...porEstado.prueba];
  const mensual = cobrando.reduce((t, r) => t + costoMensual(r), 0);

  const pruebasPorVencer = porEstado.prueba
    .map((r) => ({ rec: r, dias: diasHasta(hoy, r.fecha_inicio) }))
    .filter((x) => x.dias <= 3)
    .sort((a, b) => a.dias - b.dias);

  return {
    mensual,
    anual: mensual * 12,
    ...porEstado,
    cobrando,
    pruebasPorVencer,
  };
}

/**
 * Sugerencias al escribir el nombre: la categoría y cómo suele cobrarse.
 * Los MONTOS no se sugieren a propósito: los precios cambian, y una cifra
 * vieja precargada es peor que un campo vacío.
 */
export const CONOCIDAS = [
  { nombre: 'Netflix', categoria: 'Suscripciones digitales', icono: '🎬' },
  { nombre: 'Spotify', categoria: 'Suscripciones digitales', icono: '🎧' },
  { nombre: 'YouTube Premium', categoria: 'Suscripciones digitales', icono: '▶️' },
  { nombre: 'Disney+', categoria: 'Suscripciones digitales', icono: '🏰' },
  { nombre: 'Max', categoria: 'Suscripciones digitales', icono: '🎬' },
  { nombre: 'Prime Video', categoria: 'Suscripciones digitales', icono: '📦' },
  { nombre: 'ChatGPT', categoria: 'Suscripciones digitales', icono: '🤖', variable: true },
  { nombre: 'Claude', categoria: 'Suscripciones digitales', icono: '🤖', variable: true },
  { nombre: 'iCloud', categoria: 'Suscripciones digitales', icono: '☁️' },
  { nombre: 'Google One', categoria: 'Suscripciones digitales', icono: '☁️' },
  { nombre: 'Gimnasio', categoria: 'Gimnasio', icono: '🏋️' },
  { nombre: 'Plan de celular', categoria: 'Plan de celular', icono: '📱' },
  { nombre: 'Internet', categoria: 'Internet', icono: '🌐' },
];
